import { applyAbsoluteBoundsToObject } from '@/components/Canvas/utils/groupHelpers';

const CURVE_LETTER_CLASS = 'curve-letter';

const TextCurveMixin = {
  methods: {

    // Curving grows the box, so the editor has to fall back to the flat one. Remember how far
    // the curved box sits from it, not where it was: an offset survives a drag or a duplicate.
    applyCurvedBoundsToTextObject(objectList, object, bounds) {
      if (!object || !bounds) { return; }

      const previousX = object.X;
      const previousY = object.Y;

      applyAbsoluteBoundsToObject(objectList, object, bounds);

      object.nonCurvedOffsetX = (object.nonCurvedOffsetX || 0) + (object.X - previousX);
      object.nonCurvedOffsetY = (object.nonCurvedOffsetY || 0) + (object.Y - previousY);
    },

    traverseAndWrap(node) {
      // Convert NodeList to array and iterate
      Array.from(node.childNodes).forEach((child) => {
        this.spanEachCharacterWithFormatting(child);
      });
    },

    spanEachCharacterWithFormatting(el) {
      const fragment = document.createDocumentFragment();

      const FORMATTING_TAGS = new Set(['STRONG', 'EM', 'U']);

      const carriesColor = node => (node.style && node.style.color)
          || (node.tagName === 'FONT' && node.hasAttribute('color'));
      const carriesFont = node => (node.style && node.style.fontFamily)
          || (node.tagName === 'FONT' && node.hasAttribute('face'));

      const walk = (node, activeFormats = []) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const innerFrag = document.createDocumentFragment();

          const clonesCache = activeFormats.map(fmtNode => fmtNode.cloneNode(false));

          const text = node.textContent;
          Array.prototype.forEach.call(text, (char) => {
            const span = document.createElement('span');
            span.classList.add(CURVE_LETTER_CLASS);
            let inner = document.createTextNode(char === ' ' ? '\u00A0' : char);

            // wrap inside formatting nodes (in reverse order)
            for (let i = clonesCache.length - 1; i >= 0; i--) {
              const wrapper = clonesCache[i].cloneNode(false);
              wrapper.appendChild(inner);
              inner = wrapper;
            }
            span.appendChild(inner);
            innerFrag.appendChild(span);
          });

          fragment.appendChild(innerFrag);
        } else if (
            node.nodeType === Node.ELEMENT_NODE
            && node.tagName === 'IMG'
            && node.classList
            && node.classList.contains('emoji')
        ) {
          const span = document.createElement('span');
          span.classList.add(CURVE_LETTER_CLASS);
          let inner = node.cloneNode(true);
          for (let i = activeFormats.length - 1; i >= 0; i--) {
            const wrapper = activeFormats[i].cloneNode(false);
            wrapper.appendChild(inner);
            inner = wrapper;
          }
          span.appendChild(inner);
          fragment.appendChild(span);
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const nextFormats = (FORMATTING_TAGS.has(node.tagName) || carriesColor(node)|| carriesFont(node))
              ? activeFormats.concat(node)
              : activeFormats;

          node.childNodes.forEach((child) => {
            walk(child, nextFormats);
          });
        }
      };

      walk(el);
      el.innerHTML = '';
      el.appendChild(fragment);
    },

    curveText(elem, rds = 40, selectedObject = {}) {
      const letters = elem.querySelectorAll(`span.${CURVE_LETTER_CLASS}`);


      const comp = window.getComputedStyle(elem);
      const ch   = parseInt(comp.lineHeight, 10);
      const fs   = parseInt(comp.fontSize, 10);

      const delta   = 180 / Math.PI;
      const dir     = rds > 0 ? 1 : -1;
      const exclude = rds > 0 ? 103 : 102.7;

      const v     = Math.abs(rds) / 100;
      const eased = 1 - (2 ** (-8 * v));

      const maxRadius = 2000; // flatter
      const minRadius = 80;
      let radius      = maxRadius - eased * (maxRadius - minRadius);
      if (!radius) { radius = minRadius; }

      radius = rds == 0 ? 999999 : radius;

      elem.style.position = 'relative';


      // Cache widths to avoid layout thrash
      const widths   = new Array(letters.length);
      let totalWidth = 0;
      for (let i = 0; i < letters.length; i++) {
        const w     = letters[i].offsetWidth;
        widths[i]   = w;
        totalWidth += w;
      }


      const origin = dir === -1
          ? `center ${(-radius + ch) / fs}em`
          : `center ${radius / fs}em`;

      const innerRadius = radius - ch;
      let offset        = 0;

      // Precompute rotations from cached widths only
      for (let i = 0; i < widths.length; i++) {
        const half      = widths[i] / 2;
        offset         += (half / innerRadius) * delta;
        letters[i]._rot = offset;
        offset         += (half / innerRadius) * delta;
      }

      const halfOffset = (-offset * dir / 2);

      // Apply transforms without forcing layout

      for (let i = 0; i < letters.length; i++) {
        const l         = letters[i];
        const w         = widths[i];
        const r         = halfOffset + l._rot * dir;
        const { style } = l;


        style.setProperty('--i', i);
        style.setProperty('--total', letters.length);


        style.position        = 'absolute';
        style.left            = '50%';
        style.marginLeft      = `${-(w / 2) / fs}em`;
        style.transform       = `rotate(${r}deg)`;
        style.transformOrigin = origin;
      }
    },


    getBoundingBoxRelativeToParent(parentElement, targetContainer = null) {
      // If no target container specified, use the parent itself
      const scaledElement = document.getElementById('main_html');


      const originalTransform       = scaledElement.style.transform;
      scaledElement.style.transform = 'none';
      console.log('originalTransform');
      console.log(originalTransform);
      console.log(scaledElement.style.transform);
      const container = targetContainer || parentElement;

      const absoluteElements = Array.from(container.querySelectorAll('*'))
          .filter((element) => {
            const style = window.getComputedStyle(element);
            return style.position === 'absolute';
          });

      if (absoluteElements.length === 0) {
        return null;
      }

      const parentRect = parentElement.getBoundingClientRect();
      let minX         = Infinity;
      let minY         = Infinity;
      let maxX         = -Infinity;
      let maxY         = -Infinity;

      absoluteElements.forEach((element) => {
        const elementRect = element.getBoundingClientRect();

        // Calculate position relative to parent
        const relativeLeft   = elementRect.left - parentRect.left;
        const relativeTop    = elementRect.top - parentRect.top;
        const relativeRight  = relativeLeft + elementRect.width;
        const relativeBottom = relativeTop + elementRect.height;

        minX = Math.min(minX, relativeLeft);
        minY = Math.min(minY, relativeTop);
        maxX = Math.max(maxX, relativeRight);
        maxY = Math.max(maxY, relativeBottom);
      });
      scaledElement.style.transform = originalTransform;
      return {
        x:      minX,
        y:      minY,
        width:  maxX - minX,
        height: maxY - minY,
        right:  maxX,
        bottom: maxY,
      };
    },

    adjustTargetElement(elemSelector, selectedObject) {
      console.log('adjustTargetElement Function starts');
      const r         = parseInt(selectedObject.curve, 10);
      const parent    = document.querySelector(elemSelector);
      const container = parent
          && (parent.querySelector('.focus-text-div > div') || parent.querySelector('.focus-text-div'));
      const page      = document.getElementById('main_html');
      if (!parent || !container || !page) {
        return null;
      }
      // Group children use canvas-absolute coords; always measure against the page.
      const frame = page;

      const targetOriginalScale = parent.style.transform;
      parent.style.transform    = 'none';

      const boundingBox = this.getBoundingBoxRelativeToParent(frame, container);

      parent.style.transform = targetOriginalScale;

      if (!boundingBox) {
        return {
          x:      selectedObject.X,
          y:      selectedObject.Y,
          width:  selectedObject.Width,
          height: selectedObject.Height,
        };
      }

      let offset = 0;
      if (r < 0) {
        offset = parseInt(selectedObject.fontSize, 10) * parseInt(selectedObject.scaleX || 1, 10) * 0.8;
      }
      const fittedY = boundingBox.y - offset;

      parent.style.top    = `${fittedY}px`;
      parent.style.left   = `${boundingBox.x}px`;
      parent.style.width  = `${boundingBox.width}px`;
      parent.style.height = `${boundingBox.height}px`;

      const focusDiv = parent.querySelector('.focus-text-div');
      if (r >= 0) {
        parent.style.alignItems = 'flex-start';
        if (focusDiv) { focusDiv.style.marginBottom = '0'; }
      } else {
        parent.style.alignItems = 'flex-end';
        if (focusDiv) { focusDiv.style.marginBottom = '1.3em'; }
      }

      return {
        x:      boundingBox.x,
        y:      fittedY,
        width:  boundingBox.width,
        height: boundingBox.height,
        right:  boundingBox.x + boundingBox.width,
        bottom: fittedY + boundingBox.height,
      };
    },
  },
};


export default TextCurveMixin;
